import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useContext,
} from "react";
import {
  fetchStockRequestDetailsData,
} from "../../../services/stock-service";
import Aggrid from "../../../framework/AgGrid";
import classes from "./index.module.css";
import moment from "moment";
import { displayToast } from "../../../framework/toast/toastUtils";
import CellNumberEditor from "../../../framework/cellEditor/CellNumberEditor";
import { getStringValue } from "../../../utils/utils";
import GlobalContext from "../../../framework/GlobalContext";
import { selectRow, rowDown, rowUp } from "../../../utils/gridUtils";

const StockRequestDetails = (props) => {
  const { stockRequestId = null, onEscape = () => {} } = props;
  const [requestData, setRequestData] = useState(null);
  const [rowData, setRowData] = useState(null);
  const [api, setApi] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);
  const { currentComponent, setCurrentComponent } = useContext(GlobalContext);

  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Batch Id",
        field: "batchId",
        width: 150,
        hide: true,
      },
      {
        headerName: "Product ID",
        field: "medicineId",
        width: 150,
      },
      {
        headerName: "Product",
        field: "medicineNameDetailed",
        width: 425,
        cellRendererFramework: (params) => {
          return (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {params.data.medicineNameDetailed || params.data.medicine.medicineNameDetailed}
              {params.data.currentStockQuantity && <div
                style={{
                  backgroundColor: "transparent",
                  color: "black",
                  fontSize: "0.75rem",
                  fontWeight: "700",
                  textAlign: "center",
                  padding: "0.3125rem",
                }}
              >
                {params.data.currentStockQuantity} ::{" "}
                {params.data.currentLoose || 0}
              </div>}
            </div>
          );
        },
      },
      {
        headerName: "Requested Quantity",
        field: "quantity",
        width: 200,
      },
      {
        headerName: "Transfered Quantity",
        field: "transferedQuantity",
        width: 200,
        editable: false,
        showTotal: true,
        cellEditor: "cellNumberEditor",
        cellRendererFramework: (params) => {
          return params.colDef.editable ? (
            <div className="ag-editable-cell">
              {getStringValue(params.data.transferedQuantity)}
            </div>
          ) : (
            params?.data?.transferedQuantity || 0
          );
        },
        cellEditorParams: (params) => {
          return {
            decimalScale: 0,
            value: params.data.transferedQuantity,
            onChange: (values, e) => {
              if (values.floatValue > params.data.currentStockQuantity) {
                displayToast({
                  type: "error",
                  title: `Quantity cannot be greater than ${params.data.currentStockQuantity}`,
                });
                params.data.transferedQuantity =
                  params.data?.currentStockQuantity;
                api.redrawRows();
                return;
              }
              if (values.floatValue > params.data.quantity) {
                displayToast({
                  type: "error",
                  title: `Quantity cannot be greater than ${params.data.quantity}`,
                });
                params.data.transferedQuantity = params.data?.quantity;
                api.redrawRows();
                return;
              }
              return (params.data.transferedQuantity = values.floatValue);
            },
          };
        },
      },
      {
        headerName: "Expiry Date",
        field: "batchExpiry",
        width: 150,
        hide: true,
        valueFormatter: (params) => {
          return moment(params.value).format("DD/MM/YYYY");
        },
      },
      {
        headerName: "Remarks",
        field: "remarks",
        width: 150,
      },
    ];
  }, [api]);

  const getStockRequestDetails = useCallback((stockRequestId) => {
    fetchStockRequestDetailsData(`_id=${stockRequestId}`).then(async (resp) => {
      const data = resp?.result?.rows;
      setRequestData(data);
      const updatedRowData = await Promise.all(
        data?.stockRequestItems?.map(async (item) => {
          return {
            ...item,
            transferedQuantity: item?.transferedQuantity || item?.quantity,
          };
        }) || []
      );
      setRowData(updatedRowData);
    });
  }, []);

  const onGridReadyHandler = useCallback((params)=>{
    setApi(params.api)
    params.api.sizeColumnsToFit();
    selectRow(params.api, selectedRowIndex)
  }, [selectedRowIndex]);

  const keyupListener = useCallback(
    (event) => {
      const key = event.key?.toLowerCase();
      if (key === "escape") {
        setCurrentComponent("grid_reports");
        onEscape();
        return;
      }
      if (key === "arrowdown") {
        rowDown(api)
        return;
      }
      if (key === "arrowup") {
        rowUp(api)
        return;
      }
    },
    [api, onEscape, setCurrentComponent]
  );

  useEffect(() => {
    if (stockRequestId) {
      getStockRequestDetails(stockRequestId);
    }
  }, [getStockRequestDetails, stockRequestId]);

  useEffect(() => {
    setCurrentComponent("grid_stock_request_details");
  }, [setCurrentComponent]);

  useEffect(() => {
    if (currentComponent === "grid_stock_request_details") {
      document.addEventListener("keyup", keyupListener, false);
    }
    return () => {
      document.removeEventListener("keyup", keyupListener, false);
    };
  }, [api, currentComponent, keyupListener]);

  return (
    <div className={classes["stock-request-details"]}>
      <div className={classes["stock-request-details-header"]}>
        <div className={classes["stock-request-details-header-left"]}>
          <span>Stock Request Details - {requestData?.stockRequestId}</span>
          <span>From Branch - {requestData?.fromBranchId}</span>
          <span>To Branch - {requestData?.toBranchId}</span>
        </div>
        <span className={classes["stock-request-date"]}>
          Requested Date:{" "}
          {moment(`${requestData?.businessDate}`).format("DD/MM/YYYY hh:mm A")}
        </span>
      </div>

      <div className={classes["stock-request-details-grid"]}>
        <Aggrid
          id="grid_stock_request_details"
          rowData={rowData}
          columnDefs={columnDefs}
          frameworkComponents={{
            cellNumberEditor: CellNumberEditor,
          }}
          defaultColDef={{
            resizable: true,
            cellStyle: (params) => {
              return {
                borderRight: "1px solid #a0a0a0",
              };
            },
            suppressKeyboardEvent: (params) => true,
          }}
          onGridReady={
            (params) => {
              setTimeout(() => {
                onGridReadyHandler(params);
              }, 100);
            }
          }
          onCellClicked={(params) => {
              setSelectedRowIndex(params.rowIndex);
            }
          }
        />
      </div>
    </div>
  );
};

export default StockRequestDetails;
